import type {
  ApiResponse,
  OrganizationBillingContext,
  OrganizationBillingTaxProfileResponseData,
  OrganizationBillingVatVerificationData,
  CheckOrganizationBillingVatVerificationRequest,
} from '@fluenceur/types';
import { ErrorCode } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';
import { z } from 'zod';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import { toaster } from '@/components/ui/toaster';
import { billingContextQueryKey } from '@/hooks/queries/use-billing-context-query';
import {
  BILLING_TAX_PROFILE_QUERY_KEY,
  readBillingTaxProfile,
} from '@/hooks/queries/use-billing-tax-profile-query';
import {
  billingVatVerificationQueryKey,
  canAttemptBillingVatCheck,
  readBillingVatVerification,
  useBillingVatProfileScope,
} from '@/hooks/queries/use-billing-vat-verification-query';
import { BillingTargetChangedError } from './use-billing-action-scope';

const requestSchema = z
  .object({
    expectedProfileId: z.string().uuid(),
    expectedConsultationId: z.string().uuid().nullable(),
  })
  .strict();

export function useCheckBillingVatVerification({
  context,
  profile,
  verification,
}: {
  context: OrganizationBillingContext;
  profile?: OrganizationBillingTaxProfileResponseData;
  verification?: OrganizationBillingVatVerificationData;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const saved = readBillingTaxProfile(profile);
  const known = readBillingVatVerification(verification);
  const { scope, assertCurrent } = useBillingVatProfileScope(saved?.profile?.id);
  return useMutation<
    OrganizationBillingVatVerificationData,
    AxiosError<ApiResponse> | Error,
    CheckOrganizationBillingVatVerificationRequest,
    typeof scope
  >({
    mutationKey: [...billingVatVerificationQueryKey(scope), 'check'],
    retry: false,
    onMutate: () => {
      assertCurrent(scope);
      return scope;
    },
    mutationFn: async (request) => {
      assertCurrent(scope);
      const parsed = requestSchema.safeParse(request);
      if (
        !scope.userId ||
        !scope.organizationId ||
        !scope.brandId ||
        !scope.profileId ||
        !readBillingContext(context) ||
        context.channel !== 'stripe' ||
        context.organizationId !== scope.organizationId ||
        context.brandId !== scope.brandId ||
        !context.capabilities.canCommitPaidPlan ||
        context.cardSetup.exemption === 'impersonation' ||
        !saved?.canManage ||
        saved.organizationId !== scope.organizationId ||
        saved.brandId !== scope.brandId ||
        !canAttemptBillingVatCheck(known) ||
        known.organizationId !== scope.organizationId ||
        known.brandId !== scope.brandId ||
        known.profileId !== scope.profileId ||
        !parsed.success ||
        parsed.data.expectedProfileId !== scope.profileId
      )
        throw new Error('No authorized saved-profile VAT check');
      const response = await api.post<ApiResponse<unknown>>(
        '/dashboard/billing/vat-verification/check',
        parsed.data
      );
      assertCurrent(scope);
      const data = readBillingVatVerification(response.data.data);
      if (
        response.data.success !== true ||
        !data ||
        data.organizationId !== scope.organizationId ||
        data.brandId !== scope.brandId ||
        data.profileId !== scope.profileId
      )
        throw new Error('Invalid saved-profile VAT verdict');
      return data;
    },
    onSuccess: async (data, _request, target) => {
      if (!target) throw new BillingTargetChangedError('Billing target unavailable');
      assertCurrent(target);
      client.setQueryData(billingVatVerificationQueryKey(target), data);
      await client.invalidateQueries({
        queryKey: billingContextQueryKey(target),
        exact: true,
      });
      assertCurrent(target);
      if (data.status === 'valid')
        toaster.success({
          title: t('common.success'),
          description: t('billing.vatVerification.valid'),
        });
      else if (data.status === 'invalid' || data.status === 'unavailable')
        toaster.error({
          title: t('common.error'),
          description: t(
            data.status === 'invalid'
              ? 'billing.vatVerification.invalid'
              : 'billing.vatVerification.unavailable'
          ),
        });
    },
    onError: async (error, _request, target) => {
      if (!target || error instanceof BillingTargetChangedError) return;
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      const response = (error as AxiosError<ApiResponse>).response;
      const conflict =
        response?.status === 409 &&
        response.data?.code === ErrorCode.E_STRIPE_BILLING_TAX_PROFILE_CONFLICT;
      toaster.error({
        title: t('common.error'),
        description: t(
          conflict ? 'billing.taxProfile.conflict' : 'billing.vatVerification.checkError'
        ),
      });
      if (conflict) {
        await Promise.all([
          client.invalidateQueries({
            queryKey: billingVatVerificationQueryKey(target),
            exact: true,
          }),
          client.invalidateQueries({
            queryKey: [
              ...BILLING_TAX_PROFILE_QUERY_KEY,
              target.userId,
              target.organizationId,
              target.brandId,
            ],
            exact: true,
          }),
        ]);
      }
    },
  });
}
